// Third Party
import React from 'react';
import ReactDOMServer from 'react-dom/server';

// Namespace
import * as ProposalNS from '../Proposal';

// Components
import HoldingIcon, { Props as HoldingIconProps } from '../../../views/components/common/HoldingIcon';
import { ParenthesesLabel } from '@/views/components/misc';

// Utils
import { formatLocalizedString } from '../../../utils/i18n';
import { validIncomeStreamItem } from '../../../utils/income-stream';
import ComparisonItem from '@/types/comparison-item';
import ProposalOrientation from '@/types/proposal-orientation';
import Locale from '@/types/locale';

class IncomeStreamProposalSectionBuilder {
  newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  sectionIndex: number = 0;

  currentSection: ProposalNS.ProposalTemplateSectionElement;

  proposalItems: ComparisonItem[];

  orientation: ProposalOrientation;

  locale: Locale;

  singleItemShift: boolean;

  showBaseCurrencies?: boolean;

  constructor(proposalItems: ComparisonItem[], orientation: ProposalOrientation, locale: Locale, singleItemShift: boolean, showBaseCurrencies?: boolean) {
    this.currentSection = this.buildIncomeStreamSection('INCOME_STREAM');
    this.proposalItems = proposalItems;
    this.orientation = orientation;
    this.locale = locale;
    this.singleItemShift = singleItemShift;
    this.showBaseCurrencies = showBaseCurrencies;
  }

  buildIncomeStreamSection = (key: string): ProposalNS.ProposalTemplateSectionElement => {
    const newSection = {
      type: ProposalNS.ProposalTemplateElementType.Section,
      key,
      showTitle: false,
      sidebarControl: ProposalNS.ProposalSidebarControl.Hide,
      pageFraction: {
        Portrait: 0,
        Landscape: 0,
      },
      label: {
        'en-CA': 'Income Stream',
        'fr-CA': 'Flux de revenus',
      },
      forced: true,
      layout: {
        type: ProposalNS.ProposalTemplateElementType.Layout,
        layout: {
          type: ProposalNS.LayoutType.Grid,
          columns: 1,
        },
        elements: [],
      },
    } as ProposalNS.ProposalTemplateSectionElement;

    return newSection;
  };

  buildGraph = (itemCount: number): void => {
    const elements: ProposalNS.ProposalTemplateElement[] = [
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Text,
        },
        DAO: {
          type: ProposalNS.DAOType.Text,
          localizedTextDAOOptions: {
            texts: [
              {
                text: {
                  'en-CA': 'Income Stream',
                  'fr-CA': 'Flux de revenus',
                },
                className: 'proposal-section-header h2-title no-border',
              },
            ],
          },
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Grid,
          columns: itemCount,
          className: 'no-overflow income-stream-legend',
        },
        DAO: {
          type: ProposalNS.DAOType.IncomeStreamGraph,
        },
        toolbarOptionConfig: {
          key: 'INCOME_STREAM',
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Line,
          className: 'income-stream-graph',
          configOptions: {
            chart: {
              height: 250,
            },
            tooltip: {
              enabled: true,
            },
            yAxis: {
              gridLineWidth: 1,
              opposite: true,
              showLastLabel: false,
              labels: {
                x: 8,
                formatterType: ProposalNS.ChartLabelFormatterType.Dollars,
              },
            },
            xAxis: {
              crosshair: {
                width: 0,
              },
              labels: {
                formatterType: ProposalNS.ChartLabelFormatterType.Date,
              },
            },
          },
        },
        DAO: {
          type: ProposalNS.DAOType.IncomeStreamGraph,
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Text,
          className: 'no-border',
        },
        DAO: {
          type: ProposalNS.DAOType.Text,
          localizedTextDAOOptions: {
            texts: [
              {
                text: {
                  'en-CA': 'This chart uses Monthly Net Asset Values, converted to CAD.',
                  'fr-CA': "Ce graphique utilise les valeurs d'actif net mensuelles, converties en CAD.",
                },
                className: 'chart-disclaimer',
              },
            ],
          },
        },
      },
    ];

    this.currentSection.pageFraction[this.orientation] += this.orientation === 'Portrait' ? 0.35 : 0.45;
    this.currentSection.layout.elements = [...this.currentSection.layout.elements, ...elements];
  };

  buildSummary = (proposalItem: ComparisonItem, index: number): void => {
    const holdingIcon: string = ReactDOMServer.renderToString(
      React.createElement<HoldingIconProps>(HoldingIcon, {
        type: proposalItem.type,
        color: proposalItem.color,
      }),
    );

    const currency: string = ReactDOMServer.renderToString(
      React.createElement(ParenthesesLabel, {
        as: 'span',
        text: proposalItem.currency,
      }),
    );

    const formattedItemName = `
      <div class="income-stream-item-name">
        ${holdingIcon}
        <span>${formatLocalizedString(proposalItem.name, this.locale)}</span>
        ${this.showBaseCurrencies ? currency : ''}
      </div>
    `;

    const elements: ProposalNS.ProposalTemplateElement[] = [
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Html,
        },
        DAO: {
          type: ProposalNS.DAOType.HTMLContent,
          htmlContentDAOOptions: {
            html: [
              {
                content: {
                  'en-CA': formattedItemName,
                  'fr-CA': formattedItemName,
                },
              },
            ],
          },
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Text,
          className: 'income-stream-scenario-title h4-title',
        },
        DAO: {
          type: ProposalNS.DAOType.Text,
          localizedTextDAOOptions: {
            texts: [
              {
                text: {
                  'en-CA': 'DISTRIBUTION SCENARIOS',
                  'fr-CA': 'SCÉNARIOS DE DISTRIBUTION',
                },
              },
            ],
          },
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Grid,
          className: 'income-stream-summary-grid',
          columns: 4,
        },
        DAO: {
          type: ProposalNS.DAOType.IncomeStreamSummary,
          filterItemsByIndexDAOOptions: {
            selection: [index],
          },
        },
      },
    ];

    const newFraction = this.orientation === 'Portrait' ? 0.15 : 0.2;
    this.handlePageBreaks(newFraction);
    this.currentSection.layout.elements = [...this.currentSection.layout.elements, ...elements];
  };

  buildData = (index: number, yearCount: number): void => {
    const elements: ProposalNS.ProposalTemplateElement[] = [
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Table,
          showHeader: false,
          className: 'income-stream-data-table',
        },
        DAO: {
          type: ProposalNS.DAOType.IncomeStream,
          distributionTable: false,
          filterItemsByIndexDAOOptions: {
            selection: [index],
          },
        },
      },
    ];

    const newFraction = (50 + 30 * yearCount) / (this.orientation === 'Portrait' ? 1080 : 800);
    this.handlePageBreaks(newFraction);
    this.currentSection.layout.elements = [...this.currentSection.layout.elements, ...elements];
  };

  buildDistributions = (index: number, yearCount: number): void => {
    const elements: ProposalNS.ProposalTemplateElement[] = [
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Text,
          className: 'proposal-section-header no-border',
        },
        DAO: {
          type: ProposalNS.DAOType.Text,
          localizedTextDAOOptions: {
            texts: [
              {
                text: {
                  'en-CA': 'Source of Distribution',
                  'fr-CA': 'Source de distribution',
                },
                className: 'income-stream-scenario-title h2-title',
              },
            ],
          },
        },
      },
      {
        type: ProposalNS.ProposalTemplateElementType.Data,
        dataView: {
          type: ProposalNS.DataView.Table,
          showHeader: false,
          className: 'income-stream-distributions-table',
        },
        DAO: {
          type: ProposalNS.DAOType.IncomeStream,
          distributionTable: true,
          filterItemsByIndexDAOOptions: {
            selection: [index],
          },
        },
      },
    ];

    const newFraction = (100 + 30 * yearCount) / (this.orientation === 'Portrait' ? 1080 : 800);
    this.handlePageBreaks(newFraction);
    this.currentSection.layout.elements = [...this.currentSection.layout.elements, ...elements];
  };

  handlePageBreaks = (newFraction: number) => {
    const total: number = newFraction + this.currentSection.pageFraction[this.orientation];
    if (total > 1.0) {
      this.currentSection.pageFraction[this.orientation] = 1.0;
      this.newSections.push(this.currentSection);
      this.currentSection = this.buildIncomeStreamSection('INCOME_STREAM');
      this.currentSection.pageFraction[this.orientation] = newFraction;
    } else {
      this.currentSection.pageFraction[this.orientation] = total;
    }
  };

  build = () => {
    const filteredScenarios = this.proposalItems.filter(item => item.scenarios != null);
    this.buildGraph(filteredScenarios.length);

    for (let i = 0; i < this.proposalItems.length; i++) {
      const proposalItem = this.proposalItems[i];
      if (
        validIncomeStreamItem(proposalItem) &&
        proposalItem.scenarios != null &&
        proposalItem.scenarios.defaultIncomeStream &&
        proposalItem.scenarios.defaultIncomeStream.data
      ) {
        // the threshold that makes sense seems to be 5 items (plus current year -> 6)
        const yearCount: number = Object.keys(proposalItem.scenarios.defaultIncomeStream.data.results).length > 6 ? 10 : 6;
        const index = this.singleItemShift ? i + 1 : i;
        this.buildSummary(proposalItem, index);
        this.buildData(index, yearCount);
        this.buildDistributions(index, yearCount);
      }
    }

    this.currentSection.pageFraction[this.orientation] = 1;
    this.newSections.push(this.currentSection);
  };
}

export const buildIncomeStreamSections = (
  proposalItems: ComparisonItem[],
  orientation: ProposalOrientation,
  locale: Locale,
  singleItemShift: boolean,
  showBaseCurrencies?: boolean,
): ProposalNS.ProposalTemplateSectionElement[] => {
  if (!proposalItems.some(item => item.scenarios != null)) {
    return [];
  }

  const builder: IncomeStreamProposalSectionBuilder = new IncomeStreamProposalSectionBuilder(
    proposalItems,
    orientation,
    locale,
    singleItemShift,
    showBaseCurrencies,
  );
  builder.build();
  return builder.newSections;
};
